import { Component, Input, Type, Output, EventEmitter, ViewChild } from '@angular/core';
import { BaseModel, CommandResult, EntityType } from '@cl/shared/models';
import { BaseUpsertComponent } from '@cl/core/components/base-upsert-component';
import { BaseService } from '@cl/core/services';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ServiceFactory } from '@cl/core/services/service.factory';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
    selector: 'cl-search-field',
    templateUrl: './search-field.component.html',
    styleUrls: ['search-field.component.less'],
    standalone: false,
})
export class SearchFieldComponent {
    @ViewChild('selectObject') selectObject: NzSelectComponent;
    @ViewChild('selectId') selectId: NzSelectComponent;

    @Input()
    fieldId: string;

    @Input()
    fieldLabel: string;

    @Input()
    fieldPlaceholder: string = 'Meklēt';

    @Input()
    allowClear: boolean = false;

    @Input()
    disabled: boolean = false;

    @Input()
    idMode: boolean = false;

    @Input()
    set modelType(entityType: EntityType) {
        this.service = this.serviceFactory.getService(entityType);
    }

    _object: BaseModel;

    @Input()
    set object(val: BaseModel) {
        this._object = val;

        if (!val) {
            this.listOfOption = [];
            return;
        }

        if (!this.service?.toString) {
            return;
        }

        this.listOfOption = [
            {
                value: val,
                text: this.service.toString(val),
            },
        ];
    }
    get object(): BaseModel {
        return this._object;
    }

    @Input()
    id: number;

    @Input()
    upsertComponent: Type<BaseUpsertComponent<BaseModel, BaseService<BaseModel>>>;

    @Input() customSearchFn?: (searchText: string) => Promise<CommandResult<BaseModel[]>>;

    @Input() minimumSearchTextLengthThreshold: number = 3;

    @Output()
    idChange: EventEmitter<number> = new EventEmitter<number>();

    @Output()
    objectChange: EventEmitter<BaseModel> = new EventEmitter<BaseModel>();

    formChanged: boolean = false;

    listOfOption: Array<{ value: BaseModel; text: string }> = [];
    searchSubject: Subject<string> = new Subject<string>();

    nzFilterOption = () => true;
    service: BaseService<BaseModel>;
    isSearchPerformed: boolean;

    constructor(
        private modalService: NzModalService,
        private serviceFactory: ServiceFactory,
        private messageService: NzMessageService,
    ) {
        this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe(async (searchText: string): Promise<void> => {
            try {
                const foundOptions: BaseModel[] = this.customSearchFn
                    ? (await this.customSearchFn(searchText)).data
                    : (await this.service.search(searchText).toPromise()).data;
                this.listOfOption = [];
                foundOptions.forEach((d) => {
                    this.listOfOption.push({
                        value: d,
                        text: this.service.toString(d),
                    });
                });
                this.isSearchPerformed = true;

                // set input to be in focus again, because afther isSearchPeformed is set to true,
                // suffixActions takes over the focus, so it has to be set back for uninterupted search text inputting
                setTimeout(() => {
                    requestAnimationFrame(() => {
                        this.idMode ? this.selectId.focus() : this.selectObject.focus();
                    });
                });
            } catch (ex) {
                console.error(ex);
                if (ex.error?.errors) {
                    ex.error.errors.forEach((error) => {
                        this.messageService.error(error);
                    });
                    return;
                }

                this.messageService.error('Kļūda atlasot izvēles opcijas');
                return;
            }
        });
    }

    async search(text: string): Promise<void> {
        if (text.length < this.minimumSearchTextLengthThreshold) {
            return;
        }

        this.searchSubject.next(text);
    }

    objectSelectionChanged(newObject: BaseModel): void {
        this.objectChange.emit(newObject);
    }

    idSelectionChanged(newId: number): void {
        this.idChange.emit(newId);
    }

    async addObject(): Promise<void> {
        const modal = this.modalService.create({
            nzTitle: 'Pievienošana',
            nzContent: this.upsertComponent,
        });

        modal.afterOpen.subscribe(() => {
            const component = modal.getContentComponent();
            component.returnObject = true;
            component.fromSearchField = true;
            component.initForm();
            component.afterInit();
            component.objectForm.markAsDirtyAndUpdateAll();
        });

        modal.afterClose.subscribe(async (obj: BaseModel) => {
            if (obj) {
                this.object = obj;
                this.objectChange.emit(this.object);
                this.formChanged = true;
                this.listOfOption.push({
                    value: this.object,
                    text: this.service.toString(this.object),
                });
            }
        });
    }

    async editObject(): Promise<void> {
        const modal = this.modalService.create({
            nzTitle: 'Labošana',
            nzContent: this.upsertComponent,
        });

        const fullObjectData = this.object.id ? (await this.service.getSingle(this.object.id)).data : this.object;

        modal.afterOpen.subscribe(() => {
            const component = modal.getContentComponent();
            component.setEditMode(fullObjectData);
            component.returnObject = true;
            component.objectForm.markAsDirtyAndUpdateAll();
        });

        modal.afterClose.subscribe(async (obj: BaseModel) => {
            if (obj) {
                this.object = obj;
                this.objectChange.emit(this.object);
            }
        });
    }

    clearSelection(): void {
        if (!this.allowClear) {
            return;
        }

        this.objectSelectionChanged(null);
    }
}
